import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Calendar, User, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export interface ConflictData {
  id: string;
  operation: 'update';
  table: string;
  localData: any;
  serverData: any;
  localTimestamp: number;
  serverTimestamp: string;
}

interface ConflictResolutionModalProps {
  conflicts: ConflictData[];
  onResolve: (conflictId: string, useLocal: boolean) => void;
  onResolveAll: (useLocal: boolean) => void;
}

export const ConflictResolutionModal = ({ 
  conflicts, 
  onResolve, 
  onResolveAll 
}: ConflictResolutionModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (conflicts.length === 0) return null;

  const currentConflict = conflicts[currentIndex];
  const hasMoreConflicts = currentIndex < conflicts.length - 1;

  const formatTimestamp = (timestamp: number | string) => {
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    return format(date, "dd/MM/yyyy 'a las' HH:mm:ss", { locale: es });
  };

  const getTableName = (table: string): string => {
    const names: Record<string, string> = {
      'obligaciones_ambientales': 'Obligación Ambiental',
      'documentos_legales': 'Documento Legal',
      'documentos_sociales': 'Documento Social',
      'documentos_gdb': 'Documento GDB',
      'cumplimientos_documentos': 'Cumplimiento',
      'evidencias_cumplimiento': 'Evidencia',
    };
    return names[table] || table;
  };

  const renderDataComparison = (localData: any, serverData: any) => {
    const keys = new Set([...Object.keys(localData), ...Object.keys(serverData)]);
    const ignoredKeys = ['id', 'user_id', 'created_at', 'updated_at', 'fileData'];

    return Array.from(keys)
      .filter(key => !ignoredKeys.includes(key))
      .map(key => {
        const localValue = localData[key];
        const serverValue = serverData[key];
        const isDifferent = JSON.stringify(localValue) !== JSON.stringify(serverValue);

        if (!isDifferent) return null;

        return (
          <div key={key} className="border-b pb-2 mb-2 last:border-b-0">
            <p className="text-sm font-medium text-muted-foreground mb-1 capitalize">
              {key.replace(/_/g, ' ')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-blue-50 dark:bg-blue-950/20 p-2 rounded">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Tu versión</p>
                <p className="text-sm">{String(localValue || '-')}</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/20 p-2 rounded">
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Versión del servidor</p>
                <p className="text-sm">{String(serverValue || '-')}</p>
              </div>
            </div>
          </div>
        );
      })
      .filter(Boolean);
  };

  const handleResolve = (useLocal: boolean) => {
    onResolve(currentConflict.id, useLocal);
    
    if (hasMoreConflicts) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Conflicto de Sincronización Detectado
          </DialogTitle>
          <DialogDescription>
            Se han detectado cambios tanto en tu dispositivo como en el servidor. 
            Por favor elige qué versión deseas mantener.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contador de conflictos */}
          <div className="flex items-center justify-between">
            <Badge variant="outline">
              Conflicto {currentIndex + 1} de {conflicts.length}
            </Badge>
            <Badge>{getTableName(currentConflict.table)}</Badge>
          </div>

          {/* Información de timestamps */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Tu versión (Local)
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Modificado: {formatTimestamp(currentConflict.localTimestamp)}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Versión del Servidor
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Modificado: {formatTimestamp(currentConflict.serverTimestamp)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Comparación de datos */}
          <Card className="p-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Diferencias Encontradas
            </h4>
            <div className="space-y-2">
              {renderDataComparison(currentConflict.localData, currentConflict.serverData)}
            </div>
          </Card>

          {/* Botones de acción */}
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => handleResolve(true)}
                variant="default"
                className="w-full"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Usar Mi Versión (Local)
              </Button>
              <Button
                onClick={() => handleResolve(false)}
                variant="outline"
                className="w-full"
              >
                <User className="h-4 w-4 mr-2" />
                Usar Versión del Servidor
              </Button>
            </div>

            {/* Resolver todos */}
            {conflicts.length > 1 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  Tienes {conflicts.length} conflictos. Puedes resolverlos todos a la vez:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => onResolveAll(true)}
                    variant="secondary"
                    size="sm"
                  >
                    Usar todas mis versiones
                  </Button>
                  <Button
                    onClick={() => onResolveAll(false)}
                    variant="secondary"
                    size="sm"
                  >
                    Usar todas las del servidor
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
